import React, { useState, useEffect } from 'react';
import '../Dropdown/Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// Opções de Unidade Tributável para NF-e
const UNIDADES_TRIBUTAVEIS = [
  { valor: 'UN', label: 'UN - Unidade' },
  { valor: 'KG', label: 'KG - Quilograma' },
  { valor: 'G', label: 'G - Grama' },
  { valor: 'L', label: 'L - Litro' },
  { valor: 'ML', label: 'ML - Mililitro' },
  { valor: 'M', label: 'M - Metro' },
  { valor: 'M2', label: 'M² - Metro Quadrado' },
  { valor: 'M3', label: 'M³ - Metro Cúbico' },
  { valor: 'CX', label: 'CX - Caixa' },
  { valor: 'PCT', label: 'PCT - Pacote' },
  { valor: 'DZ', label: 'DZ - Dúzia' },
  { valor: 'PAR', label: 'PAR - Par' },
  { valor: 'PC', label: 'PC - Peça' },
  { valor: 'FD', label: 'FD - Fardo' },
  { valor: 'SC', label: 'SC - Saco' },
  { valor: 'TON', label: 'TON - Tonelada' },
];

export default function EditarNota() {
  const [notasEditaveis, setNotasEditaveis] = useState([]);
  const [notaSelecionada, setNotaSelecionada] = useState('');
  const [dadosNota, setDadosNota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState('');
  const [itensNota, setItensNota] = useState([]);

  useEffect(() => {
    carregarNotasEditaveis();
  }, []);

  const apiCall = async (endpoint, options = {}) => {
    const urls = [
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`
    ];

    for (const url of urls) {
      try {
        console.log(`Tentando conectar em: ${url}`);
        const token = getAuthToken();
        const optionsWithAuth = { ...options, headers: { ...(options.headers || {})} };
        if (token) optionsWithAuth.headers['Authorization'] = 'Bearer ' + token;
        const response = await fetch(url, optionsWithAuth);
        if (response.ok) {
          console.log(`✅ Sucesso em: ${url}`);
          return await response.json();
        }
        const errorText = await response.text();
        console.log(`❌ Falha em: ${url} - Status: ${response.status} - ${errorText}`);
        if (url === urls[urls.length - 1]) {
          throw new Error(errorText || `Erro HTTP ${response.status}`);
        }
      } catch (error) {
        console.log(`❌ Erro em: ${url} - ${error.message}`);
        if (url === urls[urls.length - 1]) {
          throw error;
        }
      }
    }
  };

  const carregarNotasEditaveis = async () => {
    try {
      setLoading(true);
      setErro('');
      
      // Buscar todas as notas fiscais
      const data = await apiCall('NotaFiscal');
      const lista = Array.isArray(data) ? data : [];
      
      // Apenas notas Pendentes podem ser editadas (antes de serem autorizadas)
      const editaveis = lista.filter(nota => {
        const status = nota.status || nota.Status || '';
        return status === 'Pendente';
      });
      
      setNotasEditaveis(editaveis);
    } catch (err) {
      console.error('Erro ao carregar notas editáveis:', err);
      setErro('Erro ao carregar notas fiscais. Verifique se o backend está rodando.');
    } finally {
      setLoading(false);
    }
  };

  const carregarDetalhesNota = async (notaId) => {
    try {
      setLoading(true);
      setErro('');
      
      const detalhes = await apiCall(`NotaFiscal/${notaId}`);
      
      // Buscar também os itens da venda vinculada
      let itensVenda = [];
      const vendaId = detalhes.vendaId || detalhes.VendaId || detalhes.DadosVenda?.Id || detalhes.DadosVenda?.id;
      if (vendaId) {
        try {
          const vendaDetalhes = await apiCall(`Vendas/${vendaId}`);
          // Tentar carregar itens se existirem
          if (vendaDetalhes.Itens || vendaDetalhes.itens) {
            itensVenda = (vendaDetalhes.Itens || vendaDetalhes.itens || []).map((item, idx) => ({
              id: item.id || item.Id || idx,
              produtoId: item.produtoId || item.ProdutoId,
              descricao: item.Produto?.Nome || item.produto?.nome || item.descricao || item.Descricao || 'Produto',
              ncm: item.Produto?.NCM || item.produto?.ncm || item.ncm || item.NCM || '00000000',
              cfop: item.Produto?.CFOP || item.produto?.cfop || item.cfop || item.CFOP || '5102',
              unidade: item.Produto?.Unidade || item.produto?.unidade || item.unidade || 'UN',
              quantidade: item.quantidade || item.Quantidade || 1,
              valorUnitario: item.valorUnitario || item.ValorUnitario || 0,
              valorTotal: item.valorTotal || item.ValorTotal || 0,
              codigoBarras: item.Produto?.GTIN || item.produto?.gtin || item.Produto?.CodigoBarras || item.produto?.codigoBarras || 'SEM GTIN',
              // Campos fiscais do produto
              aliquotaICMS: item.Produto?.AliquotaICMS || item.produto?.aliquotaICMS || item.aliquotaICMS || 0,
              valorICMS: item.valorICMS || item.ValorICMS || 0,
              aliquotaPIS: item.Produto?.AliquotaPIS || item.produto?.aliquotaPIS || item.aliquotaPIS || 0,
              valorPIS: item.valorPIS || item.ValorPIS || 0,
              aliquotaCOFINS: item.Produto?.AliquotaCOFINS || item.produto?.aliquotaCOFINS || item.aliquotaCOFINS || 0,
              valorCOFINS: item.valorCOFINS || item.ValorCOFINS || 0,
              aliquotaIPI: item.Produto?.AliquotaIPI || item.produto?.aliquotaIPI || 0,
              cst_ICMS: item.Produto?.CST_ICMS || item.produto?.csT_ICMS || '00',
              csosn: item.Produto?.CSOSN || item.produto?.csosn || '102',
              origemMercadoria: item.Produto?.OrigemMercadoria ?? item.produto?.origemMercadoria ?? 0
            }));
          } else if (vendaDetalhes.ProdutoId || vendaDetalhes.produtoId) {
            // Produto único
            const prodId = vendaDetalhes.ProdutoId || vendaDetalhes.produtoId;
            try {
              const produto = await apiCall(`Produto/${prodId}`);
              const qtd = vendaDetalhes.QuantidadeProduto || vendaDetalhes.quantidadeProduto || 1;
              itensVenda = [{
                id: 1,
                produtoId: prodId,
                descricao: produto.Nome || produto.nome || 'Produto',
                ncm: produto.NCM || produto.ncm || '00000000',
                cfop: produto.CFOP || produto.cfop || '5102',
                unidade: produto.Unidade || produto.unidade || 'UN',
                quantidade: qtd,
                valorUnitario: produto.Preco || produto.preco || 0,
                valorTotal: (produto.Preco || produto.preco || 0) * qtd,
                codigoBarras: produto.GTIN || produto.gtin || produto.CodigoBarras || produto.codigoBarras || 'SEM GTIN',
                // Campos fiscais do produto
                aliquotaICMS: produto.AliquotaICMS || produto.aliquotaICMS || 0,
                valorICMS: ((produto.Preco || produto.preco || 0) * qtd) * ((produto.AliquotaICMS || produto.aliquotaICMS || 0) / 100),
                aliquotaPIS: produto.AliquotaPIS || produto.aliquotaPIS || 0,
                valorPIS: ((produto.Preco || produto.preco || 0) * qtd) * ((produto.AliquotaPIS || produto.aliquotaPIS || 0) / 100),
                aliquotaCOFINS: produto.AliquotaCOFINS || produto.aliquotaCOFINS || 0,
                valorCOFINS: ((produto.Preco || produto.preco || 0) * qtd) * ((produto.AliquotaCOFINS || produto.aliquotaCOFINS || 0) / 100),
                aliquotaIPI: produto.AliquotaIPI || produto.aliquotaIPI || 0,
                cst_ICMS: produto.CST_ICMS || produto.csT_ICMS || '00',
                csosn: produto.CSOSN || produto.csosn || '102',
                origemMercadoria: produto.OrigemMercadoria ?? produto.origemMercadoria ?? 0
              }];
            } catch (e) {
              console.warn('Erro ao carregar produto:', e);
            }
          }
        } catch (e) {
          console.warn('Erro ao carregar venda:', e);
        }
      }

      setItensNota(itensVenda);
      
      setDadosNota({
        id: detalhes.id || detalhes.Id,
        numero: detalhes.numero || detalhes.Numero || '',
        serie: detalhes.serie || detalhes.Serie || '1',
        valorTotal: detalhes.valorTotal || detalhes.ValorTotal || 0,
        dataEmissao: detalhes.dataEmissao || detalhes.DataEmissao || '',
        ambiente: detalhes.ambiente || detalhes.Ambiente || '2',
        vendaId: vendaId,
        clienteNome: detalhes.nomeCliente || detalhes.NomeCliente || 
                     detalhes.DadosCliente?.nome || detalhes.DadosCliente?.Nome || '',
        clienteId: detalhes.DadosCliente?.Id || detalhes.DadosCliente?.id || '',
        status: detalhes.status || detalhes.Status || '',
        chaveAcesso: detalhes.chaveAcesso || detalhes.ChaveAcesso || '',
        protocoloAutorizacao: detalhes.protocoloAutorizacao || detalhes.ProtocoloAutorizacao || '',
        dataAutorizacao: detalhes.dataAutorizacao || detalhes.DataAutorizacao || '',
        tipoVenda: detalhes.tipoVenda || detalhes.TipoVenda || 'produto',
        tipoDocumento: detalhes.tipoDocumento || detalhes.TipoDocumento || 'NFe',
        natOp: (detalhes.tipoVenda || detalhes.TipoVenda) === 'servico' ? 'PRESTACAO DE SERVICOS' : 'VENDA DE MERCADORIA',
        indPag: '0', // 0=à vista, 1=a prazo, 2=outros
        modFrete: '9', // 9=sem frete
        // Totais de impostos
        totalICMS: 0,
        totalPIS: 0,
        totalCOFINS: 0
      });
    } catch (err) {
      console.error('Erro ao carregar detalhes da nota:', err);
      setErro('Erro ao carregar detalhes da nota fiscal.');
    } finally {
      setLoading(false);
    }
  };

  const handleNotaChange = (e) => {
    const notaId = e.target.value;
    setNotaSelecionada(notaId);
    
    if (notaId) {
      carregarDetalhesNota(notaId);
    } else {
      setDadosNota(null);
    }
  };

  const handleInputChange = (campo, valor) => {
    setDadosNota(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const handleItemChange = (index, campo, valor) => {
    setItensNota(prev => {
      const novosItens = [...prev];
      novosItens[index] = { ...novosItens[index], [campo]: valor };
      // Recalcular valor total do item se quantidade ou valor unitário mudar
      if (campo === 'quantidade' || campo === 'valorUnitario') {
        const qtd = campo === 'quantidade' ? parseFloat(valor) || 0 : parseFloat(novosItens[index].quantidade) || 0;
        const vUnit = campo === 'valorUnitario' ? parseFloat(valor) || 0 : parseFloat(novosItens[index].valorUnitario) || 0;
        const valorTotal = qtd * vUnit;
        novosItens[index].valorTotal = valorTotal;
        // Recalcular impostos do item
        const aliqICMS = parseFloat(novosItens[index].aliquotaICMS) || 0;
        const aliqPIS = parseFloat(novosItens[index].aliquotaPIS) || 0;
        const aliqCOFINS = parseFloat(novosItens[index].aliquotaCOFINS) || 0;
        novosItens[index].valorICMS = valorTotal * (aliqICMS / 100);
        novosItens[index].valorPIS = valorTotal * (aliqPIS / 100);
        novosItens[index].valorCOFINS = valorTotal * (aliqCOFINS / 100);
      }
      // Recalcular se alíquota mudar
      if (campo === 'aliquotaICMS' || campo === 'aliquotaPIS' || campo === 'aliquotaCOFINS') {
        const valorTotal = parseFloat(novosItens[index].valorTotal) || 0;
        if (campo === 'aliquotaICMS') novosItens[index].valorICMS = valorTotal * ((parseFloat(valor) || 0) / 100);
        if (campo === 'aliquotaPIS') novosItens[index].valorPIS = valorTotal * ((parseFloat(valor) || 0) / 100);
        if (campo === 'aliquotaCOFINS') novosItens[index].valorCOFINS = valorTotal * ((parseFloat(valor) || 0) / 100);
      }
      return novosItens;
    });
  };

  // Recalcular valor total da nota e totais de impostos baseado nos itens
  useEffect(() => {
    if (itensNota.length > 0 && dadosNota) {
      const total = itensNota.reduce((acc, item) => acc + (parseFloat(item.valorTotal) || 0), 0);
      const totalICMS = itensNota.reduce((acc, item) => acc + (parseFloat(item.valorICMS) || 0), 0);
      const totalPIS = itensNota.reduce((acc, item) => acc + (parseFloat(item.valorPIS) || 0), 0);
      const totalCOFINS = itensNota.reduce((acc, item) => acc + (parseFloat(item.valorCOFINS) || 0), 0);
      if (total !== dadosNota.valorTotal || totalICMS !== dadosNota.totalICMS) {
        setDadosNota(prev => ({ ...prev, valorTotal: total, totalICMS, totalPIS, totalCOFINS }));
      }
    }
  }, [itensNota]);

  const handleSalvar = async (e) => {
    e.preventDefault();
    
    if (!dadosNota) {
      setErro('Nenhuma nota fiscal selecionada.');
      return;
    }

    try {
      setLoading(true);
      setErro('');
      setMensagem('');

      // Montar payload com todos os dados da NF e itens
      const payload = {
        id: dadosNota.id,
        numero: dadosNota.numero,
        serie: dadosNota.serie,
        valorTotal: parseFloat(dadosNota.valorTotal),
        dataEmissao: dadosNota.dataEmissao,
        ambiente: dadosNota.ambiente,
        natOp: dadosNota.natOp,
        indPag: dadosNota.indPag,
        modFrete: dadosNota.modFrete,
        tipoVenda: dadosNota.tipoVenda,
        tipoDocumento: dadosNota.tipoDocumento,
        itens: itensNota.map(item => ({
          produtoId: item.produtoId,
          descricao: item.descricao,
          ncm: item.ncm,
          cfop: item.cfop,
          unidade: item.unidade,
          quantidade: parseFloat(item.quantidade),
          valorUnitario: parseFloat(item.valorUnitario),
          valorTotal: parseFloat(item.valorTotal),
          codigoBarras: item.codigoBarras || 'SEM GTIN',
          aliquotaICMS: parseFloat(item.aliquotaICMS) || 0,
          valorICMS: parseFloat(item.valorICMS) || 0,
          aliquotaPIS: parseFloat(item.aliquotaPIS) || 0,
          valorPIS: parseFloat(item.valorPIS) || 0,
          aliquotaCOFINS: parseFloat(item.aliquotaCOFINS) || 0,
          valorCOFINS: parseFloat(item.valorCOFINS) || 0,
          origemMercadoria: parseInt(item.origemMercadoria) || 0,
          cst_ICMS: item.cst_ICMS || '00',
          csosn: item.csosn || '102'
        }))
      };

      console.log('Dados para salvar:', payload);
      
      // Tentar salvar via PUT
      try {
        const resultado = await apiCall(`NotaFiscal/${dadosNota.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (resultado && (resultado.success !== false)) {
          setMensagem(`✅ Nota Fiscal #${dadosNota.numero} atualizada com sucesso!`);
          setTimeout(() => {
            setNotaSelecionada('');
            setDadosNota(null);
            setItensNota([]);
            carregarNotasEditaveis();
            setMensagem('');
          }, 3000);
        } else {
          setErro(resultado?.message || 'Erro ao salvar nota fiscal.');
        }
      } catch (putErr) {
        // Se não tiver endpoint PUT, mostrar aviso
        console.warn('PUT não disponível, mostrando dados:', putErr);
        setMensagem(`⚠️ Dados validados. Backend pode não ter endpoint PUT implementado ainda. Payload: ${JSON.stringify(payload, null, 2)}`);
      }
    } catch (err) {
      console.error('Erro ao salvar nota:', err);
      setErro(`Erro ao salvar: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data) => {
    if (!data) return '';
    const d = new Date(data);
    const ano = d.getFullYear();
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f0f0f0', 
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <div style={{ 
        width: '100%', 
        maxWidth: '900px',
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ 
          color: 'rgb(235, 133, 0)', 
          fontSize: '28px', 
          fontWeight: 'bold',
          marginBottom: '10px',
          textAlign: 'center'
        }}>
          Editar Nota Fiscal
        </h1>
        
        <div style={{
          background: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '25px'
        }}>
          <p style={{ 
            margin: 0,
            color: '#0d47a1',
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            ℹ️ <strong>INFORMAÇÃO:</strong> Apenas notas fiscais com status <strong>Pendente</strong> podem ser editadas.
            <br />
            Após a autorização, os dados não podem mais ser alterados.
          </p>
        </div>

        {erro && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #ef5350'
          }}>
            {erro}
          </div>
        )}

        {mensagem && (
          <div style={{
            background: '#fff3e0',
            color: 'rgb(184, 98, 0)',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgb(250, 178, 65)'
          }}>
            {mensagem}
          </div>
        )}

        <div style={{ marginBottom: '30px' }}>
          <label style={{ 
            display: 'block',
            marginBottom: '10px',
            color: '#333',
            fontWeight: '500',
            fontSize: '15px'
          }}>
            Selecione a Nota Fiscal:
          </label>
          <select
            value={notaSelecionada}
            onChange={handleNotaChange}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '15px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="">-- Selecione uma Nota Fiscal --</option>
            {notasEditaveis.map((nota) => {
              const id = nota.id || nota.Id || nota.notaId;
              const numero = nota.numero || nota.Numero || '';
              const nomeCliente = nota.nomeCliente || nota.NomeCliente || nota.Cliente?.Nome || '';
              const valor = nota.valor || nota.Valor || nota.valorTotal || nota.ValorTotal || 0;
              
              return (
                <option key={id} value={id}>
                  NF #{numero} - {nomeCliente} - R$ {Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </option>
              );
            })}
          </select>
          
          {notasEditaveis.length === 0 && !loading && (
            <p style={{ 
              marginTop: '10px', 
              color: '#666', 
              fontSize: '14px',
              fontStyle: 'italic'
            }}>
              Nenhuma nota fiscal disponível para edição.
            </p>
          )}
        </div>

        {dadosNota && (
          <form onSubmit={handleSalvar}>
            {/* Seção: Dados da Nota Fiscal */}
            <h3 style={{ 
              color: '#333', 
              fontSize: '18px', 
              marginBottom: '15px',
              paddingBottom: '10px',
              borderBottom: '2px solid rgb(235, 133, 0)'
            }}>
              📄 Dados da Nota Fiscal
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500', fontSize: '14px' }}>
                  Número da NF:
                </label>
                <input
                  type="text"
                  value={dadosNota.numero}
                  onChange={(e) => handleInputChange('numero', e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500', fontSize: '14px' }}>
                  Série:
                </label>
                <input
                  type="text"
                  value={dadosNota.serie}
                  onChange={(e) => handleInputChange('serie', e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500', fontSize: '14px' }}>
                  Data de Emissão:
                </label>
                <input
                  type="date"
                  value={formatarData(dadosNota.dataEmissao)}
                  onChange={(e) => handleInputChange('dataEmissao', e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500', fontSize: '14px' }}>
                  Ambiente:
                </label>
                <select
                  value={dadosNota.ambiente}
                  onChange={(e) => handleInputChange('ambiente', e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', cursor: 'pointer' }}
                  required
                >
                  <option value="2">2 - Homologação</option>
                  <option value="1">1 - Produção</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500', fontSize: '14px' }}>
                  Natureza da Operação:
                </label>
                <input
                  type="text"
                  value={dadosNota.natOp || 'VENDA'}
                  onChange={(e) => handleInputChange('natOp', e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
                  placeholder="Ex: VENDA, DEVOLUÇÃO, etc."
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500', fontSize: '14px' }}>
                  Indicador de Pagamento:
                </label>
                <select
                  value={dadosNota.indPag || '0'}
                  onChange={(e) => handleInputChange('indPag', e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', cursor: 'pointer' }}
                >
                  <option value="0">0 - À Vista</option>
                  <option value="1">1 - A Prazo</option>
                  <option value="2">2 - Outros</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500', fontSize: '14px' }}>
                  Modalidade do Frete:
                </label>
                <select
                  value={dadosNota.modFrete || '9'}
                  onChange={(e) => handleInputChange('modFrete', e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', cursor: 'pointer' }}
                >
                  <option value="0">0 - Por conta do Emitente</option>
                  <option value="1">1 - Por conta do Destinatário</option>
                  <option value="2">2 - Por conta de Terceiros</option>
                  <option value="9">9 - Sem Frete</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500', fontSize: '14px' }}>
                  Valor Total (R$):
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={dadosNota.valorTotal}
                  disabled
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }}
                  title="Calculado automaticamente a partir dos itens"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500', fontSize: '14px' }}>
                  Tipo de Venda:
                </label>
                <select
                  value={dadosNota.tipoVenda || 'produto'}
                  onChange={(e) => {
                    const tipo = e.target.value;
                    handleInputChange('tipoVenda', tipo);
                    handleInputChange('natOp', tipo === 'servico' ? 'PRESTACAO DE SERVICOS' : 'VENDA DE MERCADORIA');
                    handleInputChange('tipoDocumento', tipo === 'servico' ? 'NFS-e' : 'NFe');
                  }}
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', cursor: 'pointer' }}
                >
                  <option value="produto">Produto (NF-e)</option>
                  <option value="servico">Serviço (NFS-e)</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500', fontSize: '14px' }}>
                  Tipo Documento:
                </label>
                <input
                  type="text"
                  value={dadosNota.tipoDocumento || 'NFe'}
                  disabled
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', cursor: 'not-allowed' }}
                  title="Determinado pelo tipo de venda"
                />
              </div>
            </div>

            {/* Resumo de Impostos */}
            {itensNota.length > 0 && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px',
                background: '#f3e5f5', padding: '15px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #ce93d8'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6a1b9a', fontWeight: 'bold' }}>Total Produtos</div>
                  <div style={{ fontSize: '18px', color: '#4a148c', fontWeight: 'bold' }}>
                    R$ {Number(dadosNota.valorTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6a1b9a', fontWeight: 'bold' }}>Total ICMS</div>
                  <div style={{ fontSize: '18px', color: '#4a148c', fontWeight: 'bold' }}>
                    R$ {Number(dadosNota.totalICMS || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6a1b9a', fontWeight: 'bold' }}>Total PIS</div>
                  <div style={{ fontSize: '18px', color: '#4a148c', fontWeight: 'bold' }}>
                    R$ {Number(dadosNota.totalPIS || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6a1b9a', fontWeight: 'bold' }}>Total COFINS</div>
                  <div style={{ fontSize: '18px', color: '#4a148c', fontWeight: 'bold' }}>
                    R$ {Number(dadosNota.totalCOFINS || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            )}

            {/* Seção: Campos NÃO Editáveis */}
            <h3 style={{ 
              color: '#666', 
              fontSize: '16px', 
              marginBottom: '15px',
              paddingBottom: '10px',
              borderBottom: '1px solid #ddd'
            }}>
              🔒 Dados Somente Leitura
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#666', fontWeight: '500', fontSize: '14px' }}>
                  Cliente:
                </label>
                <input
                  type="text"
                  value={dadosNota.clienteNome || ''}
                  disabled
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: '#f5f5f5', color: '#666', cursor: 'not-allowed' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#666', fontWeight: '500', fontSize: '14px' }}>
                  Status:
                </label>
                <input
                  type="text"
                  value={dadosNota.status || ''}
                  disabled
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: '#f5f5f5', color: '#666', cursor: 'not-allowed' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#666', fontWeight: '500', fontSize: '14px' }}>
                  ID da Venda:
                </label>
                <input
                  type="text"
                  value={dadosNota.vendaId || ''}
                  disabled
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: '#f5f5f5', color: '#666', cursor: 'not-allowed' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#666', fontWeight: '500', fontSize: '14px' }}>
                  Tipo da Venda:
                </label>
                <input
                  type="text"
                  value={dadosNota.tipoVenda === 'servico' ? 'Serviço' : 'Produto'}
                  disabled
                  style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: dadosNota.tipoVenda === 'servico' ? '#e3f2fd' : '#fff3e0', color: dadosNota.tipoVenda === 'servico' ? '#1565c0' : 'rgb(184, 98, 0)', cursor: 'not-allowed', fontWeight: 'bold' }}
                />
              </div>

              {dadosNota.chaveAcesso && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#666', fontWeight: '500', fontSize: '14px' }}>
                    Chave de Acesso:
                  </label>
                  <input
                    type="text"
                    value={dadosNota.chaveAcesso}
                    disabled
                    style={{ width: '100%', padding: '10px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: '#f5f5f5', color: '#666', cursor: 'not-allowed', fontFamily: 'monospace' }}
                  />
                </div>
              )}

              {dadosNota.protocoloAutorizacao && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#666', fontWeight: '500', fontSize: '14px' }}>
                    Protocolo de Autorização:
                  </label>
                  <input
                    type="text"
                    value={dadosNota.protocoloAutorizacao}
                    disabled
                    style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: '#f5f5f5', color: '#666', cursor: 'not-allowed' }}
                  />
                </div>
              )}
            </div>

            {/* Seção: Itens da Nota Fiscal */}
            <h3 style={{ 
              color: '#333', 
              fontSize: '18px', 
              marginBottom: '15px',
              paddingBottom: '10px',
              borderBottom: '2px solid rgb(235, 133, 0)'
            }}>
              📦 Itens da Nota Fiscal ({itensNota.length} {itensNota.length === 1 ? 'item' : 'itens'})
            </h3>

            {itensNota.length === 0 ? (
              <div style={{
                background: '#fff3e0',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px',
                textAlign: 'center',
                color: 'rgb(184, 98, 0)'
              }}>
                ⚠️ Nenhum item encontrado para esta nota fiscal.
              </div>
            ) : (
              itensNota.map((item, index) => (
                <div key={item.id || index} style={{
                  background: '#fafafa',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '15px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <span style={{ fontWeight: 'bold', color: 'rgb(235, 133, 0)' }}>
                      Item #{index + 1}
                    </span>
                    <span style={{ 
                      background: '#e8f5e9', 
                      color: '#2e7d32', 
                      padding: '4px 12px', 
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      R$ {Number(item.valorTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '15px'
                  }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontWeight: '500', fontSize: '13px' }}>
                        Descrição do Produto:
                      </label>
                      <input
                        type="text"
                        value={item.descricao || ''}
                        onChange={(e) => handleItemChange(index, 'descricao', e.target.value)}
                        style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontWeight: '500', fontSize: '13px' }}>
                        NCM:
                      </label>
                      <input
                        type="text"
                        value={item.ncm || ''}
                        onChange={(e) => handleItemChange(index, 'ncm', e.target.value)}
                        style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
                        placeholder="00000000"
                        maxLength={8}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontWeight: '500', fontSize: '13px' }}>
                        CFOP:
                      </label>
                      <input
                        type="text"
                        value={item.cfop || ''}
                        onChange={(e) => handleItemChange(index, 'cfop', e.target.value)}
                        style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
                        placeholder="5102"
                        maxLength={4}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontWeight: '500', fontSize: '13px' }}>
                        Unidade Tributável:
                      </label>
                      <select
                        value={item.unidade || 'UN'}
                        onChange={(e) => handleItemChange(index, 'unidade', e.target.value)}
                        style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', cursor: 'pointer' }}
                      >
                        {UNIDADES_TRIBUTAVEIS.map(u => (
                          <option key={u.valor} value={u.valor}>{u.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontWeight: '500', fontSize: '13px' }}>
                        Quantidade:
                      </label>
                      <input
                        type="number"
                        step="0.0001"
                        value={item.quantidade || 0}
                        onChange={(e) => handleItemChange(index, 'quantidade', e.target.value)}
                        style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
                        min="0"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontWeight: '500', fontSize: '13px' }}>
                        Valor Unitário (R$):
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={item.valorUnitario || 0}
                        onChange={(e) => handleItemChange(index, 'valorUnitario', e.target.value)}
                        style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
                        min="0"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontWeight: '500', fontSize: '13px' }}>
                        Código de Barras (GTIN):
                      </label>
                      <input
                        type="text"
                        value={item.codigoBarras || 'SEM GTIN'}
                        onChange={(e) => handleItemChange(index, 'codigoBarras', e.target.value)}
                        style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: item.codigoBarras && item.codigoBarras !== 'SEM GTIN' ? '#e8f5e9' : '#fff3e0', color: item.codigoBarras && item.codigoBarras !== 'SEM GTIN' ? '#2e7d32' : 'rgb(184, 98, 0)' }}
                        placeholder="SEM GTIN (do cadastro do produto)"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#666', fontWeight: '500', fontSize: '13px' }}>
                        Valor Total Item (R$):
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={item.valorTotal || 0}
                        disabled
                        style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold', cursor: 'not-allowed' }}
                      />
                    </div>
                  </div>

                  {/* Campos de Impostos do Item */}
                  <div style={{ marginTop: '12px', padding: '12px', background: '#f3e5f5', borderRadius: '6px', border: '1px solid #ce93d8' }}>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#6a1b9a', marginBottom: '10px' }}>
                      💰 Impostos do Item (calculados a partir do cadastro do produto)
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#555', fontWeight: '500', fontSize: '12px' }}>Alíq. ICMS (%):</label>
                        <input type="number" step="0.01" value={item.aliquotaICMS || 0}
                          onChange={(e) => handleItemChange(index, 'aliquotaICMS', e.target.value)}
                          style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ce93d8', borderRadius: '4px', outline: 'none' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#555', fontWeight: '500', fontSize: '12px' }}>Valor ICMS:</label>
                        <input type="number" step="0.01" value={Number(item.valorICMS || 0).toFixed(2)} disabled
                          style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ce93d8', borderRadius: '4px', background: '#ede7f6', color: '#4a148c', fontWeight: 'bold', cursor: 'not-allowed' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#555', fontWeight: '500', fontSize: '12px' }}>Alíq. PIS (%):</label>
                        <input type="number" step="0.01" value={item.aliquotaPIS || 0}
                          onChange={(e) => handleItemChange(index, 'aliquotaPIS', e.target.value)}
                          style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ce93d8', borderRadius: '4px', outline: 'none' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#555', fontWeight: '500', fontSize: '12px' }}>Valor PIS:</label>
                        <input type="number" step="0.01" value={Number(item.valorPIS || 0).toFixed(2)} disabled
                          style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ce93d8', borderRadius: '4px', background: '#ede7f6', color: '#4a148c', fontWeight: 'bold', cursor: 'not-allowed' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#555', fontWeight: '500', fontSize: '12px' }}>Alíq. COFINS (%):</label>
                        <input type="number" step="0.01" value={item.aliquotaCOFINS || 0}
                          onChange={(e) => handleItemChange(index, 'aliquotaCOFINS', e.target.value)}
                          style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ce93d8', borderRadius: '4px', outline: 'none' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#555', fontWeight: '500', fontSize: '12px' }}>Valor COFINS:</label>
                        <input type="number" step="0.01" value={Number(item.valorCOFINS || 0).toFixed(2)} disabled
                          style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ce93d8', borderRadius: '4px', background: '#ede7f6', color: '#4a148c', fontWeight: 'bold', cursor: 'not-allowed' }} />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}

            <div style={{ 
              display: 'flex', 
              gap: '15px', 
              justifyContent: 'flex-end',
              marginTop: '30px'
            }}>
              <button
                type="button"
                onClick={() => {
                  setNotaSelecionada('');
                  setDadosNota(null);
                  setItensNota([]);
                }}
                style={{
                  padding: '12px 30px',
                  fontSize: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >
                Cancelar
              </button>
              
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '12px 30px',
                  fontSize: '15px',
                  border: 'none',
                  borderRadius: '6px',
                  background: loading ? '#ccc' : 'rgb(235, 133, 0)',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.target.style.background = 'rgb(208, 116, 0)';
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.target.style.background = 'rgb(235, 133, 0)';
                }}
              >
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        )}
      </div>

      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}
